import { createSelector } from 'reselect'
import {
  getTourV2IdForCheckoutItem,
  isLuxPlusSubscriptionItem,
  isTourV2ExperienceItem,
  isSubscriptionJoinItem,
  isTourV2Item,
  isNoProtectionInsuranceItem,
  isInsuranceItem, isBedbankItem,
  isExperienceTicketViewItem,
  isExperienceItem,
  isLEHotelItem,
} from 'lib/checkout/checkoutUtils'
import { subDays } from 'lib/datetime/dateUtils'
import moment from 'moment'
import { ISO_DATE_FORMAT } from 'constants/dateFormats'
import { sum } from 'lib/array/arrayUtils'
import { getInsuranceTotals } from '../insurance'
import getAllItemsDiscountTotals from '../getAllItemsDiscountTotals'
import { checkoutWithMemberPrice } from 'checkout/selectors/view/luxPlusSubscription'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'
import getAllItemViews from 'checkout/selectors/view/getAllItemViews'
import { getAllOffers } from 'selectors/offerSelectors'
import { getAgentHubCommissionItemKey } from 'lib/agentHub/agentHubUtils'
import { AGENT_HUB_PAYMENT_MODE } from 'constants/payment'
import getExperienceItemsView from 'checkout/selectors/view/getExperienceItemsView'
import isPaymentScheduleAvailable from './isPaymentScheduleAvailable'
import { getBedbankRateKey } from 'actions/BedbankOfferActions'

function mapItemType(item: App.Checkout.AnyItem): string {
  if (isTourV2Item(item)) {
    return 'tour_v2'
  }
  if (isTourV2ExperienceItem(item)) {
    return 'tour_optional_experience'
  }
  if (isBedbankItem(item)) {
    return 'bedbank'
  }
  if (isLEHotelItem(item)) {
    return 'accommodation'
  }
  if (isLuxPlusSubscriptionItem(item) || isSubscriptionJoinItem(item)) {
    return 'subscription'
  }
  if (isExperienceTicketViewItem(item)) {
    return 'experience'
  }
  return item.itemType
}

function mapOfferType(item: App.Checkout.AnyItem, offers: Record<string, App.AnyOffer>): string | undefined {
  if (!isLEHotelItem(item)) {
    return undefined
  }
  const offer = offers[item.offerId]
  return offer?.type
}

const getPaymentScheduleRequestInfo = createSelector(
  (state: App.State) => isPaymentScheduleAvailable(state),
  (state: App.State) => getInsuranceTotals(state),
  (state: App.State) => getAllItemsDiscountTotals(state),
  (state: App.State) => checkoutWithMemberPrice(state),
  (state: App.State) => state.checkout.cart.items,
  (state: App.State) => getAllOffers(state),
  (state: App.State) => getAllItemViews(state),
  (state: App.State) => getExperienceItemsView(state),
  (state: App.State) => state.checkout.commissionData?.itemsCommission,
  (state: App.State) => state.checkout.payment.agentHubPaymentMode,
  (state: App.State) => state.offer.bedbankOfferRates,
  (state: App.State) => state.geo.currentCurrency,
  (state: App.State) => state.checkout.payment.useCredit,
  (
    isPaymentScheduleAvailable,
    insuranceTotals,
    discountTotalToCartItemsMap,
    checkoutWithMemberPrice,
    cartItems,
    offers,
    itemViews,
    experienceItemViews,
    itemsCommission,
    agentHubPaymentMode,
    bedbankOfferRates,
    currency,
    isCreditApplied,
  ): App.Checkout.PaymentScheduleRequestInfo | null => {
    if (!isPaymentScheduleAvailable) {
      return null
    }

    const shouldUseAgentHubCommission = isAgentHubEnabled && agentHubPaymentMode === AGENT_HUB_PAYMENT_MODE.PAY_NET_AMOUNT

    const getPaymentScheduleInfoFromOffer = (item: App.Checkout.TourV2Item | App.Checkout.BedbankHotelItem | App.Checkout.LEHotelItem) => {
      if (isTourV2Item(item)) {
        const tourV2Offer = offers[getTourV2IdForCheckoutItem(item)] as App.Tours.TourV2Offer
        return tourV2Offer?.paymentScheduleInfo
      } else if (isBedbankItem(item)) {
        const rateKey = getBedbankRateKey([item.occupancy], item.checkIn, item.checkOut)
        const bedbankRates = bedbankOfferRates[item.offerId]?.[rateKey]
        const rate = bedbankRates?.find(rate => rate.id === item.roomRateId)
        return rate?.paymentScheduleInfo
      } else if (isLEHotelItem(item)) {
        const hotelOffer = offers[item.offerId] as App.HotelOffer
        return hotelOffer?.paymentScheduleInfo
      }
    }

    const getStartDateFromItem = (item: App.Checkout.TourV2Item | App.Checkout.BedbankHotelItem | App.Checkout.LEHotelItem): string | undefined => {
      if (isTourV2Item(item)) {
        return item.startDate
      } else if (isBedbankItem(item)) {
        return 'checkIn' in item ? item.checkIn : undefined
      } else if (isLEHotelItem(item)) {
        return 'checkIn' in item ? item.checkIn : undefined
      }
      return undefined
    }

    // Define item type configurations with priority order
    const itemTypeConfigs = [
      {
        type: 'tour',
        items: cartItems.filter(isTourV2Item),
        hasPaymentScheduleInfo: getPaymentScheduleInfoFromOffer,
      },
      {
        type: 'bedbank',
        items: cartItems.filter(isBedbankItem),
        hasPaymentScheduleInfo: getPaymentScheduleInfoFromOffer,
      },
      {
        type: 'hotel',
        items: cartItems.filter(isLEHotelItem),
        hasPaymentScheduleInfo: getPaymentScheduleInfoFromOffer,
      },
    ]

    // Find the first item with a template ID across all item types
    let itemWithTemplateId: App.Checkout.TourV2Item | App.Checkout.BedbankHotelItem | App.Checkout.LEHotelItem | undefined

    for (const config of itemTypeConfigs) {
      itemWithTemplateId = config.items.find(config.hasPaymentScheduleInfo)
      if (itemWithTemplateId) {
        break
      }
    }

    if (!itemWithTemplateId) {
      return null
    }

    const paymentScheduleInfo = getPaymentScheduleInfoFromOffer(itemWithTemplateId)

    if (!paymentScheduleInfo?.templateId) {
      return null
    }

    if (!paymentScheduleInfo.supplierPaymentNumberOfDays) {
      return null
    }
    // For bedbank items, we need to get the total from the item view
    if (isBedbankItem(itemWithTemplateId)) {
      const bedbankItemView = itemViews.data.get(itemWithTemplateId.itemId)?.data as App.Checkout.BedbankAccommodationItemView | undefined
      if (!bedbankItemView?.totals?.price) {
        return null
      }
    } else if (isLEHotelItem(itemWithTemplateId)) {
      const hotelItemView = itemViews.data.get(itemWithTemplateId.itemId)?.data as App.Checkout.AccommodationItemView | undefined
      if (!hotelItemView?.totals?.price) {
        return null
      }
    } else if (!itemWithTemplateId.total) {
      return null
    }

    const startDate = getStartDateFromItem(itemWithTemplateId)

    if (!startDate) {
      return null
    }

    const dueDate = subDays(new Date(startDate), paymentScheduleInfo.supplierPaymentNumberOfDays)

    const items = cartItems
      .filter(item => !(isSubscriptionJoinItem(item) && item.waived))
      .filter(item => !isNoProtectionInsuranceItem(item))
      .map((item) => {
        let total: number | undefined = undefined
        let discountTotal = discountTotalToCartItemsMap.has(item.itemId) ? discountTotalToCartItemsMap.get(item.itemId) : 0
        if (isLuxPlusSubscriptionItem(item) || isSubscriptionJoinItem(item)) {
          total = item.amount ?? undefined
        } else if (isInsuranceItem(item)) {
          total = insuranceTotals.hasRequiredData ? insuranceTotals.data.price : 0
        } else if (isBedbankItem(item)) {
          const bedbankItemView = itemViews.data.get(item.itemId)?.data as App.Checkout.BedbankAccommodationItemView | undefined
          total = (checkoutWithMemberPrice && bedbankItemView?.totals?.memberPrice) ? bedbankItemView?.totals?.memberPrice : bedbankItemView?.totals?.price ?? undefined
        } else if (isExperienceItem(item)) {
          const itemView = experienceItemViews.data.find(view => view.id === item.itemId)
          total = (checkoutWithMemberPrice && item.memberTotal) ? item.memberTotal : item.total
          // discounts are stored at the ticket level for experiences, so we have to manually get it from the ticket views
          const discountTotals = itemView?.ticketViews?.map((ticketView) => discountTotalToCartItemsMap.get(ticketView.item.itemId)) ?? []
          if (discountTotals.every(total => total !== undefined)) {
            discountTotal = sum(discountTotals)
          }
        } else if (isLEHotelItem(item)) {
          const hotelItemView = itemViews.data.get(item.itemId)?.data as App.Checkout.AccommodationItemView | undefined
          total = (checkoutWithMemberPrice && hotelItemView?.totals?.memberPrice) ? hotelItemView?.totals?.memberPrice : hotelItemView?.totals?.price ?? undefined
        } else {
          total = (checkoutWithMemberPrice && item.memberTotal) ? item.memberTotal : item.total
        }

        const commission = shouldUseAgentHubCommission ? itemsCommission?.[getAgentHubCommissionItemKey(item, cartItems)]?.itemDiscount : 0

        return {
          idItems: item.itemId,
          total,
          type: mapItemType(item),
          discountTotal: shouldUseAgentHubCommission && commission ? commission : discountTotal,
          offerType: mapOfferType(item, offers),
        }
      })

    // We don't want to call payment schedule endpoint if any item has an undefined total or discountTotal
    if (!items.every((item): item is typeof item & { total: number, discountTotal: number } => item.total !== undefined && item.discountTotal !== undefined)) {
      return null
    }

    return {
      templateId: paymentScheduleInfo.templateId,
      bookingInfo: {
        bookingDate: moment().format(ISO_DATE_FORMAT),
        checkinDate: startDate,
        supplierDueDate: moment(dueDate).format(ISO_DATE_FORMAT),
        isCreditApplied,
        currency,
        items,
      },
    }
  },
)

export default getPaymentScheduleRequestInfo
